/**
 * Extra flagship Practice components — Ohm's Circuit, Parallelogram, DNA, Mitosis.
 * Each is a self-contained interactive practice UI with rule-based feedback.
 */
import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, ArrowRight, RotateCcw } from "lucide-react";

// -------------- Small helpers --------------
const Panel = ({ title, stepIdx, total, testid = "pflex-instruction", children }) => (
  <div className="rounded-2xl bg-slate-900/80 border border-cyan-500/30 p-5 shadow-xl">
    <div className="flex items-center gap-2 text-cyan-400 text-xs font-bold uppercase tracking-widest mb-2">
      <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 text-xs font-black">{stepIdx + 1}</div>
      Step {stepIdx + 1} of {total} · Practice
    </div>
    <p className="text-slate-100 leading-relaxed" data-testid={testid}>{title}</p>
    {children}
  </div>
);

const Feedback = ({ msg }) => msg ? (
  <div className={`rounded-2xl p-4 border ${msg.type === "ok" ? "bg-emerald-900/40 border-emerald-500/50 text-emerald-200" : msg.type === "warn" ? "bg-amber-900/40 border-amber-500/50 text-amber-200" : "bg-rose-900/40 border-rose-500/50 text-rose-200"}`} data-testid="practice-feedback">
    <div className="flex gap-2">
      {msg.type === "ok" ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />}
      <div className="text-sm leading-relaxed">{msg.msg}</div>
    </div>
  </div>
) : null;

const NextBar = ({ enabled, isLast, onNext, onReset }) => (
  <div className="flex gap-2">
    <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 flex-1" onClick={onReset} data-testid="practice-reset">
      <RotateCcw className="mr-2 h-4 w-4" /> Reset
    </Button>
    <Button size="sm" className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold rounded-full" onClick={onNext} disabled={!enabled} data-testid="practice-next">
      {isLast ? "Go to Observe" : "Next step"} <ArrowRight className="ml-2 h-4 w-4" />
    </Button>
  </div>
);

// =========================================================================
// OHM'S CIRCUIT
// =========================================================================
export const OhmPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [battery, setBattery] = useState(false);
  const [bulbs, setBulbs] = useState(0);
  const [mode, setMode] = useState(null); // series | parallel
  const [voltage, setVoltage] = useState(6);
  const [measured, setMeasured] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [stepStatus, setStepStatus] = useState({});
  const current = useMemo(() => {
    if (!mode) return 0;
    const R = mode === "series" ? 6 : 1.5;
    return voltage / R;
  }, [mode, voltage]);
  const step = steps[stepIdx];

  const advance = (id) => {
    setStepStatus((s) => ({ ...s, [id]: "ok" }));
    setFeedback({ type: "ok", msg: step.correct_hint });
  };

  const dropBattery = () => { setBattery(true); advance("p1"); };
  const dropBulb = () => {
    if (bulbs < 2) {
      setBulbs((b) => b + 1);
      if (bulbs + 1 === 2) advance("p2");
    }
  };
  const chooseMode = (m) => {
    setMode(m);
    // Accept any wiring mode with a reasonable voltage (3–12V).
    if (voltage >= 3 && voltage <= 12) {
      advance("p3");
    } else if (voltage > 12) {
      setFeedback({ type: "warn", msg: "Careful — over 12V will blow the bulb here. Bring it down to 12 V or less." });
    }
  };
  const measure = () => {
    setMeasured(true);
    setFeedback({ type: "ok", msg: `Measured current I = ${current.toFixed(2)} A. ${step.correct_hint}` });
    setStepStatus((s) => ({ ...s, p4: "ok" }));
  };
  const reset = () => { setBattery(false); setBulbs(0); setMode(null); setVoltage(6); setMeasured(false); setFeedback(null); };
  const doNext = () => {
    if (stepIdx < steps.length - 1) { setStepIdx(stepIdx + 1); setFeedback(null); }
    else onComplete?.();
  };
  const canNext = stepStatus[step?.id] === "ok";

  return (
    <div className="h-full flex flex-col lg:flex-row gap-4">
      {/* Circuit board SVG */}
      <div className="flex-1 min-h-[500px] rounded-2xl bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-800 p-4 relative">
        <svg viewBox="0 0 700 500" className="w-full h-full" data-testid="practice-canvas">
          {/* Wire border */}
          <rect x="80" y="120" width="540" height="260" fill="none" stroke={mode ? "#22d3ee" : "#475569"} strokeWidth="4" rx="8" />

          {/* Battery slot */}
          <g data-testid="battery-slot" onClick={dropBattery} style={{ cursor: battery ? "default" : "pointer" }}>
            <rect x="60" y="220" width="80" height="60" rx="6" fill={battery ? "#0f172a" : "#1e293b"} stroke={battery ? "#22c55e" : "#334155"} strokeWidth="2" strokeDasharray={battery ? "" : "6 4"} />
            {battery ? (
              <>
                <line x1="90" y1="240" x2="90" y2="260" stroke="#22c55e" strokeWidth="4" />
                <line x1="110" y1="235" x2="110" y2="265" stroke="#22c55e" strokeWidth="4" />
                <text x="100" y="205" fill="#22c55e" fontSize="12" textAnchor="middle" fontFamily="monospace">{voltage}V</text>
              </>
            ) : (
              <text x="100" y="255" fill="#64748b" fontSize="10" textAnchor="middle">tap: Battery</text>
            )}
          </g>

          {/* Bulb slots */}
          {[0, 1].map((i) => (
            <g key={i} data-testid={`bulb-slot-${i}`} onClick={dropBulb} style={{ cursor: bulbs > i ? "default" : "pointer" }} transform={`translate(${300 + i * 140}, 180)`}>
              <circle cx="0" cy="0" r="35" fill={bulbs > i ? (mode ? "#fde047" : "#1e293b") : "#1e293b"} stroke={bulbs > i ? "#eab308" : "#334155"} strokeWidth="2" strokeDasharray={bulbs > i ? "" : "6 4"} opacity={bulbs > i && mode ? Math.min(1, current * 0.25 + 0.3) : 1} />
              {bulbs > i ? (
                <>
                  <circle cx="0" cy="0" r="8" fill="#fef08a" opacity={mode ? 0.9 : 0.3} />
                  <text x="0" y="55" fill="#eab308" fontSize="10" textAnchor="middle" fontFamily="monospace">Bulb {i + 1}</text>
                </>
              ) : (
                <text x="0" y="4" fill="#64748b" fontSize="10" textAnchor="middle">tap: Bulb</text>
              )}
            </g>
          ))}

          {/* Ammeter */}
          <g data-testid="ammeter" onClick={mode && battery && bulbs === 2 ? measure : null} transform="translate(350, 350)" style={{ cursor: mode ? "pointer" : "default" }}>
            <circle cx="0" cy="0" r="30" fill="#0f172a" stroke={measured ? "#22c55e" : "#475569"} strokeWidth="2" />
            <text x="0" y="-6" fill="#22d3ee" fontSize="9" textAnchor="middle" fontFamily="monospace">A</text>
            <text x="0" y="9" fill={measured ? "#22c55e" : "#64748b"} fontSize="11" textAnchor="middle" fontFamily="monospace" fontWeight="bold">{measured ? current.toFixed(2) : "--"}</text>
            <text x="0" y="50" fill="#94a3b8" fontSize="9" textAnchor="middle">Ammeter</text>
          </g>

          {/* Wire connections */}
          {battery && bulbs > 0 && (
            <g stroke={mode ? "#22d3ee" : "#475569"} strokeWidth="2" fill="none">
              <line x1="140" y1="250" x2="300" y2="180" />
              <line x1="335" y1="180" x2="405" y2="180" />
              <line x1="475" y1="180" x2="620" y2="180" />
              <line x1="620" y1="180" x2="620" y2="350" />
              <line x1="380" y1="350" x2="620" y2="350" />
              <line x1="320" y1="350" x2="140" y2="350" />
              <line x1="140" y1="280" x2="140" y2="350" />
            </g>
          )}
        </svg>
      </div>

      {/* Control panel */}
      <div className="w-full lg:w-96 flex flex-col gap-3">
        <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />

        {stepIdx === 2 && (
          <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Wiring mode</div>
            <div className="flex gap-2">
              <Button size="sm" className={`flex-1 rounded-full ${mode === "series" ? "bg-cyan-500 text-slate-950" : "bg-slate-800 text-slate-300"}`} onClick={() => chooseMode("series")} data-testid="mode-series">Series</Button>
              <Button size="sm" className={`flex-1 rounded-full ${mode === "parallel" ? "bg-cyan-500 text-slate-950" : "bg-slate-800 text-slate-300"}`} onClick={() => chooseMode("parallel")} data-testid="mode-parallel">Parallel</Button>
            </div>
            <label className="block text-xs text-slate-400">Voltage: <span className="text-cyan-300 font-mono font-bold">{voltage} V</span></label>
            <input type="range" min="3" max="15" value={voltage} onChange={(e) => setVoltage(+e.target.value)} className="w-full accent-cyan-500" data-testid="voltage-slider" />
            {voltage > 12 && <div className="text-amber-300 text-xs">⚠ over 12V will blow the bulb</div>}
          </div>
        )}

        {stepIdx === 3 && mode && !measured && (
          <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={measure} data-testid="ammeter-btn">Read ammeter</Button>
        )}

        <Feedback msg={feedback} />
        <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
      </div>
    </div>
  );
};

// =========================================================================
// PARALLELOGRAM OF FORCES
// =========================================================================
export const ParallelogramPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [P, setP] = useState(2);
  const [Q, setQ] = useState(2);
  const [angle, setAngle] = useState(90);
  const [feedback, setFeedback] = useState(null);
  const [stepStatus, setStepStatus] = useState({});
  const step = steps[stepIdx];

  const R = useMemo(() => Math.sqrt(P * P + Q * Q + 2 * P * Q * Math.cos(angle * Math.PI / 180)), [P, Q, angle]);

  const check = () => {
    const s = step;
    if (s.id === "p1") {
      if (P >= 3.5 && P <= 4.5) { setStepStatus((x) => ({ ...x, p1: "ok" })); setFeedback({ type: "ok", msg: s.correct_hint }); }
      else setFeedback({ type: "warn", msg: `Set P to about 4 (current: ${P.toFixed(1)}).` });
    } else if (s.id === "p2") {
      if (Q >= 2.5 && Q <= 3.5) { setStepStatus((x) => ({ ...x, p2: "ok" })); setFeedback({ type: "ok", msg: s.correct_hint }); }
      else setFeedback({ type: "warn", msg: `Set Q to about 3 (current: ${Q.toFixed(1)}).` });
    } else if (s.id === "p3") {
      if (angle >= 55 && angle <= 65) { setStepStatus((x) => ({ ...x, p3: "ok" })); setFeedback({ type: "ok", msg: s.correct_hint }); }
      else if (angle < 10) setFeedback({ type: "warn", msg: s.mistakes[0].hint });
      else if (angle > 170) setFeedback({ type: "warn", msg: s.mistakes[1].hint });
      else setFeedback({ type: "warn", msg: `Adjust angle to about 60° (current: ${angle}°).` });
    } else if (s.id === "p4") {
      setStepStatus((x) => ({ ...x, p4: "ok" }));
      setFeedback({ type: "ok", msg: `Measured resultant R = ${R.toFixed(2)} N. ${s.correct_hint}` });
    }
  };

  const reset = () => { setP(2); setQ(2); setAngle(90); setFeedback(null); };
  const doNext = () => {
    if (stepIdx < steps.length - 1) { setStepIdx(stepIdx + 1); setFeedback(null); }
    else onComplete?.();
  };
  const canNext = stepStatus[step?.id] === "ok";

  // Vector rendering: origin at (400, 300); P along +x; Q at angle from P.
  const scale = 50;
  const Px = 400 + P * scale, Py = 300;
  const Qx = 400 + Q * Math.cos(angle * Math.PI / 180) * scale;
  const Qy = 300 - Q * Math.sin(angle * Math.PI / 180) * scale;
  const Rx = 400 + (P + Q * Math.cos(angle * Math.PI / 180)) * scale;
  const Ry = 300 - Q * Math.sin(angle * Math.PI / 180) * scale;

  return (
    <div className="h-full flex flex-col lg:flex-row gap-4">
      <div className="flex-1 min-h-[500px] rounded-2xl bg-slate-950 border border-slate-800 relative">
        <svg viewBox="0 0 800 500" className="w-full h-full" data-testid="practice-canvas">
          {[...Array(16)].map((_, i) => <line key={`v${i}`} x1={i * 50} y1="0" x2={i * 50} y2="500" stroke="#1e293b" strokeWidth="0.5" />)}
          {[...Array(10)].map((_, i) => <line key={`h${i}`} x1="0" y1={i * 50} x2="800" y2={i * 50} stroke="#1e293b" strokeWidth="0.5" />)}
          <circle cx="400" cy="300" r="6" fill="#f59e0b" />
          {/* P arrow */}
          <line x1="400" y1="300" x2={Px} y2={Py} stroke="#22d3ee" strokeWidth="3" />
          <polygon points={`${Px},${Py-6} ${Px},${Py+6} ${Px+10},${Py}`} fill="#22d3ee" />
          <text x={Px + 12} y={Py + 4} fill="#22d3ee" fontSize="14" fontFamily="monospace">P={P.toFixed(1)}</text>
          {/* Q arrow */}
          <line x1="400" y1="300" x2={Qx} y2={Qy} stroke="#a855f7" strokeWidth="3" />
          <polygon points={`${Qx + 8 * Math.cos((angle+90)*Math.PI/180)},${Qy - 8 * Math.sin((angle+90)*Math.PI/180)} ${Qx + 8 * Math.cos((angle-90)*Math.PI/180)},${Qy - 8 * Math.sin((angle-90)*Math.PI/180)} ${Qx + 12 * Math.cos(angle*Math.PI/180)},${Qy - 12 * Math.sin(angle*Math.PI/180)}`} fill="#a855f7" />
          <text x={Qx + 8} y={Qy - 8} fill="#a855f7" fontSize="14" fontFamily="monospace">Q={Q.toFixed(1)}</text>
          {/* Parallelogram (dashed) */}
          <line x1={Px} y1={Py} x2={Rx} y2={Ry} stroke="#475569" strokeWidth="1.5" strokeDasharray="4 3" />
          <line x1={Qx} y1={Qy} x2={Rx} y2={Ry} stroke="#475569" strokeWidth="1.5" strokeDasharray="4 3" />
          {/* Resultant (orange) */}
          <line x1="400" y1="300" x2={Rx} y2={Ry} stroke="#f59e0b" strokeWidth="3.5" />
          <text x={Rx + 8} y={Ry - 8} fill="#f59e0b" fontSize="16" fontFamily="monospace" fontWeight="bold">R={R.toFixed(2)}</text>
          {/* Angle arc */}
          <path d={`M ${400 + 30} 300 A 30 30 0 0 0 ${400 + 30 * Math.cos(angle*Math.PI/180)} ${300 - 30 * Math.sin(angle*Math.PI/180)}`} fill="none" stroke="#22d3ee" strokeWidth="1.5" />
          <text x="440" y="290" fill="#22d3ee" fontSize="12" fontFamily="monospace">θ={angle}°</text>
        </svg>
      </div>
      <div className="w-full lg:w-96 flex flex-col gap-3">
        <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 space-y-3">
          <label className="block text-xs text-slate-400">Force P: <span className="text-cyan-300 font-mono font-bold">{P.toFixed(1)}</span></label>
          <input type="range" min="1" max="6" step="0.1" value={P} onChange={(e) => setP(+e.target.value)} className="w-full accent-cyan-500" data-testid="P-slider" />
          <label className="block text-xs text-slate-400">Force Q: <span className="text-fuchsia-300 font-mono font-bold">{Q.toFixed(1)}</span></label>
          <input type="range" min="1" max="6" step="0.1" value={Q} onChange={(e) => setQ(+e.target.value)} className="w-full accent-fuchsia-500" data-testid="Q-slider" />
          <label className="block text-xs text-slate-400">Angle θ: <span className="text-amber-300 font-mono font-bold">{angle}°</span></label>
          <input type="range" min="0" max="180" value={angle} onChange={(e) => setAngle(+e.target.value)} className="w-full accent-amber-500" data-testid="angle-slider" />
        </div>
        <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={check} data-testid="check-btn">Check my values</Button>
        <Feedback msg={feedback} />
        <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
      </div>
    </div>
  );
};

// =========================================================================
// DNA BASE PAIRING
// =========================================================================
const DNA_PAIR = { A: "T", T: "A", G: "C", C: "G" };
const TEMPLATE = ["A", "T", "G", "C", "G", "A"];

export const DNAPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [placed, setPlaced] = useState(Array(TEMPLATE.length).fill(null));
  const [feedback, setFeedback] = useState(null);
  const [stepStatus, setStepStatus] = useState({});

  const dropBase = (baseIdx, base) => {
    setPlaced((prev) => {
      const copy = [...prev];
      copy[baseIdx] = base;
      return copy;
    });
    const correct = DNA_PAIR[TEMPLATE[baseIdx]] === base;
    if (correct) {
      setFeedback({ type: "ok", msg: `Correct! ${TEMPLATE[baseIdx]} pairs with ${base}.` });
      const filled = placed.filter((b, i) => (i === baseIdx ? true : b)).length + (placed[baseIdx] ? 0 : 0);
      // Ok cadence: p1 done after 1 correct, p2 after 3+, p3 after all, p4 = check
    } else {
      setFeedback({ type: "warn", msg: `${TEMPLATE[baseIdx]} does not pair with ${base}. Remember: A↔T, G↔C.` });
    }
  };

  const allCorrect = placed.every((b, i) => b && DNA_PAIR[TEMPLATE[i]] === b);
  const anyCorrect = placed.some((b, i) => b && DNA_PAIR[TEMPLATE[i]] === b);
  const threePlus = placed.filter((b, i) => b && DNA_PAIR[TEMPLATE[i]] === b).length >= 3;

  React.useEffect(() => {
    setStepStatus((s) => ({
      ...s,
      p1: anyCorrect ? "ok" : s.p1,
      p2: threePlus ? "ok" : s.p2,
      p3: allCorrect ? "ok" : s.p3,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed]);

  const checkAll = () => {
    if (allCorrect) {
      setFeedback({ type: "ok", msg: "All pairings correct! Beautiful DNA fragment." });
      setStepStatus((s) => ({ ...s, p4: "ok" }));
    } else {
      const wrong = placed.map((b, i) => (b && DNA_PAIR[TEMPLATE[i]] !== b ? i + 1 : null)).filter(Boolean);
      const empty = placed.map((b, i) => (!b ? i + 1 : null)).filter(Boolean);
      let msg = "";
      if (empty.length) msg += `Empty positions: ${empty.join(", ")}. `;
      if (wrong.length) msg += `Wrong at positions: ${wrong.join(", ")}. `;
      setFeedback({ type: "warn", msg });
    }
  };

  const reset = () => { setPlaced(Array(TEMPLATE.length).fill(null)); setFeedback(null); };
  const doNext = () => {
    if (stepIdx < steps.length - 1) { setStepIdx(stepIdx + 1); setFeedback(null); }
    else onComplete?.();
  };
  const canNext = stepStatus[steps[stepIdx]?.id] === "ok";

  const step = steps[stepIdx];
  const BASE_COLORS = { A: "#22d3ee", T: "#f59e0b", G: "#a855f7", C: "#22c55e" };

  return (
    <div className="h-full flex flex-col lg:flex-row gap-4">
      <div className="flex-1 min-h-[500px] rounded-2xl bg-slate-950 border border-slate-800 p-6 flex flex-col items-center justify-center gap-6" data-testid="practice-canvas">
        {/* Template strand */}
        <div className="flex gap-2">
          {TEMPLATE.map((b, i) => (
            <div key={`t${i}`} className="w-14 h-14 rounded-lg flex items-center justify-center text-xl font-bold border-2" style={{ background: BASE_COLORS[b] + "22", borderColor: BASE_COLORS[b], color: BASE_COLORS[b] }}>
              {b}
            </div>
          ))}
        </div>
        <div className="text-slate-500 font-mono text-xs">↑ Template strand · Drop the matching base below ↓</div>
        {/* Complement slots */}
        <div className="flex gap-2">
          {TEMPLATE.map((b, i) => {
            const val = placed[i];
            const isCorrect = val && DNA_PAIR[b] === val;
            const isWrong = val && DNA_PAIR[b] !== val;
            return (
              <div
                key={`c${i}`}
                className={`w-14 h-14 rounded-lg flex items-center justify-center text-xl font-bold border-2 transition-all ${isCorrect ? "bg-emerald-900/40 border-emerald-500 text-emerald-300" : isWrong ? "bg-rose-900/40 border-rose-500 text-rose-300" : "bg-slate-800 border-slate-700 border-dashed text-slate-500"}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const base = e.dataTransfer.getData("base");
                  if (base) dropBase(i, base);
                }}
                data-testid={`dna-slot-${i}`}
              >
                {val || "?"}
              </div>
            );
          })}
        </div>
        {/* Palette */}
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 text-center">Base palette · drag onto a slot</div>
          <div className="flex gap-3 justify-center">
            {["A", "T", "G", "C"].map((b) => (
              <div
                key={b}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("base", b)}
                className="w-16 h-16 rounded-lg flex items-center justify-center text-2xl font-bold cursor-grab active:cursor-grabbing border-2 hover:scale-110 transition-transform"
                style={{ background: BASE_COLORS[b] + "33", borderColor: BASE_COLORS[b], color: BASE_COLORS[b] }}
                data-testid={`dna-palette-${b}`}
              >
                {b}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full lg:w-96 flex flex-col gap-3">
        <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
        {stepIdx === 3 && (
          <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={checkAll} data-testid="dna-check-btn">Check all pairings</Button>
        )}
        <Feedback msg={feedback} />
        <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
      </div>
    </div>
  );
};

// =========================================================================
// MITOSIS PHASE ORDERING
// =========================================================================
const CORRECT_ORDER = ["Interphase", "Prophase", "Metaphase", "Anaphase", "Telophase"];
const PHASE_ICONS = { Interphase: "🧬", Prophase: "🌀", Metaphase: "⚖️", Anaphase: "🔀", Telophase: "🔵" };

export const MitosisPractice = ({ steps, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [slots, setSlots] = useState(Array(5).fill(null));
  const [available] = useState(() => [...CORRECT_ORDER].sort(() => Math.random() - 0.5));
  const [used, setUsed] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [stepStatus, setStepStatus] = useState({});
  const step = steps[stepIdx];

  const dropPhase = (slotIdx, phase) => {
    const isCorrect = CORRECT_ORDER[slotIdx] === phase;
    setSlots((prev) => {
      const copy = [...prev];
      if (copy[slotIdx]) return prev;
      copy[slotIdx] = phase;
      return copy;
    });
    // Only permanently remove the card from palette if it lands correctly.
    if (isCorrect) {
      setUsed((u) => [...u, phase]);
      setFeedback({ type: "ok", msg: `Correct — ${phase} at position ${slotIdx + 1}.` });
    } else {
      // Bounce the card back to the palette after 1.2s so student can retry.
      setFeedback({ type: "warn", msg: `${phase} doesn't belong at position ${slotIdx + 1}. Try again — the card will bounce back.` });
      setTimeout(() => {
        setSlots((prev) => {
          const copy = [...prev];
          if (copy[slotIdx] === phase) copy[slotIdx] = null;
          return copy;
        });
      }, 1200);
    }
  };

  const allCorrect = slots.every((p, i) => p === CORRECT_ORDER[i]);
  const firstCorrect = slots[0] === "Interphase";
  const secondCorrect = slots[1] === "Prophase";

  React.useEffect(() => {
    setStepStatus((s) => ({
      ...s,
      p1: firstCorrect ? "ok" : s.p1,
      p2: firstCorrect && secondCorrect ? "ok" : s.p2,
      p3: allCorrect ? "ok" : s.p3,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const checkAll = () => {
    if (allCorrect) {
      setFeedback({ type: "ok", msg: "All phases in correct order — Interphase → Prophase → Metaphase → Anaphase → Telophase." });
      setStepStatus((s) => ({ ...s, p4: "ok" }));
    } else {
      const wrong = slots.map((p, i) => (p && p !== CORRECT_ORDER[i] ? `${i + 1}: ${p}→${CORRECT_ORDER[i]}` : null)).filter(Boolean);
      setFeedback({ type: "warn", msg: `Fix these: ${wrong.join(", ") || "(some slots are empty)"}` });
    }
  };

  const reset = () => { setSlots(Array(5).fill(null)); setUsed([]); setFeedback(null); };
  const doNext = () => {
    if (stepIdx < steps.length - 1) { setStepIdx(stepIdx + 1); setFeedback(null); }
    else onComplete?.();
  };
  const canNext = stepStatus[step?.id] === "ok";

  return (
    <div className="h-full flex flex-col lg:flex-row gap-4">
      <div className="flex-1 min-h-[400px] rounded-2xl bg-slate-950 border border-slate-800 p-4 sm:p-6 flex flex-col justify-center gap-6 overflow-x-auto" data-testid="practice-canvas">
        <div className="text-center text-xs uppercase tracking-widest text-slate-500 font-bold">Correct sequence — drop phase cards in order</div>
        <div className="flex gap-2 justify-center flex-wrap">
          {slots.map((p, i) => {
            const correct = p && p === CORRECT_ORDER[i];
            const wrong = p && p !== CORRECT_ORDER[i];
            return (
              <div
                key={i}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { const phase = e.dataTransfer.getData("phase"); if (phase) dropPhase(i, phase); }}
                data-testid={`mitosis-slot-${i}`}
                className={`w-[calc(20%-8px)] min-w-[100px] max-w-[140px] aspect-[4/5] rounded-xl flex flex-col items-center justify-center gap-1 border-2 transition-all p-1 ${correct ? "bg-emerald-900/40 border-emerald-500" : wrong ? "bg-rose-900/40 border-rose-500" : "bg-slate-800 border-slate-700 border-dashed"}`}
              >
                <div className="text-[10px] text-slate-500 font-mono">Pos {i + 1}</div>
                <div className="text-3xl sm:text-4xl">{p ? PHASE_ICONS[p] : "?"}</div>
                <div className={`text-[11px] sm:text-sm font-bold text-center leading-tight ${correct ? "text-emerald-300" : wrong ? "text-rose-300" : "text-slate-500"}`}>{p || "empty"}</div>
              </div>
            );
          })}
        </div>
        <div>
          <div className="text-center text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Available phase cards</div>
          <div className="flex gap-2 justify-center flex-wrap">
            {available.filter((p) => !used.includes(p)).map((p) => (
              <div
                key={p}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("phase", p)}
                data-testid={`mitosis-card-${p}`}
                className="w-24 sm:w-28 h-20 sm:h-24 rounded-xl bg-slate-800 border-2 border-cyan-500/40 flex flex-col items-center justify-center gap-1 cursor-grab active:cursor-grabbing hover:scale-105 transition-transform p-1"
              >
                <div className="text-2xl sm:text-3xl">{PHASE_ICONS[p]}</div>
                <div className="text-cyan-300 font-bold text-[11px] sm:text-sm text-center">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full lg:w-96 flex flex-col gap-3">
        <Panel title={step?.instruction} stepIdx={stepIdx} total={steps.length} />
        {stepIdx === 3 && (
          <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full" onClick={checkAll} data-testid="mitosis-check-btn">Check order</Button>
        )}
        <Feedback msg={feedback} />
        <NextBar enabled={canNext} isLast={stepIdx === steps.length - 1} onNext={doNext} onReset={reset} />
      </div>
    </div>
  );
};
